// Command registry + tab completion for the Career CLI shell pane.
//
// Every command is a pure function (args, ctx) -> CommandResult. The hook
// (useShell) owns the cwd/history/output state and feeds it in through
// ShellContext. Side-effects (open PDF, open Rubik, jump to /classic) are
// signalled via the `action` field so TmuxLayout's existing action bridge
// handles them.
//
// Tab completion supports two positions:
//   - command position (first token, no trailing space) -> match command names
//   - argument position (later tokens or after a space) -> match paths in the
//     filesystem, relative to cwd

import {
  readDir,
  readFile,
  resolvePath,
  isDir,
  isFile,
  isBinary,
  tree as fsTree,
  prettyPath,
  BINARY_MARKER,
  type DirEntry,
} from './filesystem';

// ---------------------------------------------------------------------------
// Public types
// ---------------------------------------------------------------------------

export type ShellAction = 'open-rubik' | 'open-resume-pdf' | 'go-classic';

export interface ShellContext {
  cwd: string;
  setCwd: (next: string) => void;
  history: string[];
}

export interface CommandResult {
  /** Lines pushed as stdout (gray-100). May be multi-line via "\n". */
  stdout?: string;
  /** Lines pushed as stderr (red-300). */
  stderr?: string;
  /** Reset the output buffer before applying stdout/stderr. */
  clear?: boolean;
  /** Side-effect to fire after the command resolves. */
  action?: ShellAction;
}

export interface Command {
  brief: string;
  usage?: string;
  handler: (args: string[], ctx: ShellContext) => CommandResult;
}

// ---------------------------------------------------------------------------
// Tokenizer — whitespace split, no quote handling for v1.
// ---------------------------------------------------------------------------

export function tokenize(line: string): string[] {
  return line.trim().split(/\s+/).filter(Boolean);
}

// ---------------------------------------------------------------------------
// Commands
// ---------------------------------------------------------------------------

function formatDirEntries(entries: DirEntry[]): string {
  // ls output: directories suffixed with `/`, simple newline-separated.
  return entries
    .map((e) => (e.type === 'dir' ? e.name + '/' : e.name))
    .join('\n');
}

const ls: Command = {
  brief: 'list directory contents',
  usage: 'ls [path]',
  handler: (args, ctx) => {
    const target = args[0] ?? '.';
    const abs = resolvePath(target, ctx.cwd);
    if (!abs) return { stderr: `ls: ${target}: invalid path` };
    if (isFile(abs)) return { stdout: target };
    if (!isDir(abs)) return { stderr: `ls: ${target}: No such file or directory` };
    const entries = readDir(abs);
    if (!entries) return { stderr: `ls: ${target}: No such file or directory` };
    if (entries.length === 0) return {};
    return { stdout: formatDirEntries(entries) };
  },
};

const cd: Command = {
  brief: 'change directory',
  usage: 'cd [path]',
  handler: (args, ctx) => {
    const target = args[0] ?? '~';
    const abs = resolvePath(target, ctx.cwd);
    if (!abs) return { stderr: `cd: ${target}: invalid path` };
    if (!isDir(abs)) return { stderr: `cd: ${target}: Not a directory` };
    ctx.setCwd(abs);
    return {};
  },
};

const cat: Command = {
  brief: 'print file contents',
  usage: 'cat <file>',
  handler: (args, ctx) => {
    if (args.length === 0) return { stderr: 'cat: missing file operand' };
    const out: string[] = [];
    for (const target of args) {
      const abs = resolvePath(target, ctx.cwd);
      if (!abs || !isFile(abs)) {
        return { stderr: `cat: ${target}: No such file or directory` };
      }
      if (isBinary(abs)) {
        return {
          stderr: `cat: ${target}: binary file — try \`open ${target}\``,
        };
      }
      const txt = readFile(abs);
      if (txt === null || txt === BINARY_MARKER) {
        return { stderr: `cat: ${target}: cannot read` };
      }
      out.push(txt);
    }
    return { stdout: out.join('\n') };
  },
};

const treeCmd: Command = {
  brief: 'tree view of a directory',
  usage: 'tree [path]',
  handler: (args, ctx) => {
    const target = args[0] ?? '.';
    const abs = resolvePath(target, ctx.cwd);
    if (!abs) return { stderr: `tree: ${target}: invalid path` };
    if (!isDir(abs)) return { stderr: `tree: ${target}: Not a directory` };
    return { stdout: fsTree(abs).join('\n') };
  },
};

const pwd: Command = {
  brief: 'print current directory',
  handler: (_args, ctx) => ({ stdout: prettyPath(ctx.cwd) }),
};

const clear: Command = {
  brief: 'clear the terminal',
  handler: () => ({ clear: true }),
};

const history: Command = {
  brief: 'show command history',
  handler: (_args, ctx) => {
    if (ctx.history.length === 0) return {};
    const w = String(ctx.history.length).length;
    const lines = ctx.history.map(
      (cmd, i) => `${String(i + 1).padStart(w, ' ')}  ${cmd}`,
    );
    return { stdout: lines.join('\n') };
  },
};

function walkFind(
  abs: string,
  pattern: string,
  acc: string[],
  fromCwd: string,
): void {
  const entries = readDir(abs);
  if (!entries) return;
  for (const e of entries) {
    const childAbs = abs.endsWith('/') ? abs + e.name : abs + '/' + e.name;
    // Show paths relative to the search root for readability.
    const display = childAbs.startsWith(fromCwd + '/')
      ? './' + childAbs.slice(fromCwd.length + 1)
      : prettyPath(childAbs);
    if (!pattern || e.name.toLowerCase().includes(pattern.toLowerCase())) {
      acc.push(display + (e.type === 'dir' ? '/' : ''));
    }
    if (e.type === 'dir') walkFind(childAbs, pattern, acc, fromCwd);
  }
}

const find: Command = {
  brief: 'find files by substring',
  usage: 'find <pattern>',
  handler: (args, ctx) => {
    const pattern = args[0] ?? '';
    const acc: string[] = [];
    walkFind(ctx.cwd, pattern, acc, ctx.cwd);
    if (acc.length === 0) return { stdout: '(no matches)' };
    return { stdout: acc.join('\n') };
  },
};

const whoami: Command = {
  brief: 'print effective user',
  handler: () => ({ stdout: 'thanh.nguyen' }),
};

const dateCmd: Command = {
  brief: 'print current date',
  handler: () => ({ stdout: new Date().toString() }),
};

const open: Command = {
  brief: 'open a file in its native viewer',
  usage: 'open <target>',
  handler: (args, ctx) => {
    if (args.length === 0) return { stderr: 'open: missing target' };
    const target = args[0];

    // Symbolic targets that bypass path resolution. `rubik` and `rubik.cube`
    // stay as legacy aliases — the modal they open now hosts the
    // career.log timeline, not a Rubik cube — but `log` / `career.log` /
    // `timeline` are the canonical names going forward.
    if (
      target === 'log' ||
      target === 'career.log' ||
      target === 'timeline' ||
      target === 'rubik' ||
      target === 'rubik.cube'
    ) {
      return {
        stdout: 'opening career.log…',
        action: 'open-rubik',
      };
    }
    if (target === 'classic' || target === '/classic') {
      return { stdout: 'switching to classic view...', action: 'go-classic' };
    }

    // Path-resolved targets.
    const abs = resolvePath(target, ctx.cwd);
    if (!abs || !isFile(abs)) {
      return { stderr: `open: ${target}: No such file` };
    }
    if (target.endsWith('resume.pdf') || abs.endsWith('/resume.pdf')) {
      return {
        stdout: 'opening resume.pdf in a new tab...',
        action: 'open-resume-pdf',
      };
    }
    return {
      stderr: `open: ${target}: no native viewer — try \`cat ${target}\``,
    };
  },
};

const exit: Command = {
  brief: 'leave the terminal for /classic',
  handler: () => ({
    stdout: 'logout — switching to classic view...',
    action: 'go-classic',
  }),
};

// Top-level shortcut for the career.log modal. Same action as `open rubik` /
// `open log`; lives as its own command so visitors who muscle-memory typed
// `/rubik` in the chat panes find it here too (the leading `/` is stripped
// by the dispatcher in useShell).
const rubik: Command = {
  brief: 'open the career.log modal (alias of `open log`)',
  handler: () => ({
    stdout: 'opening career.log…',
    action: 'open-rubik',
  }),
};

const vim: Command = {
  brief: 'the inescapable text editor',
  handler: (args) => {
    if (args.length === 0) {
      return {
        stdout: "you can't escape. there is no vim here. just :q in your head.",
      };
    }
    return {
      stdout:
        'vim is not real on this terminal. try `cat <file>` for read, or admire from afar.',
    };
  },
};

const man: Command = {
  brief: 'manual page for a command',
  usage: 'man <command>',
  handler: (args) => {
    if (args.length === 0) {
      return { stderr: 'What manual page do you want?' };
    }
    const name = args[0];
    const cmd = COMMANDS[name];
    if (!cmd) {
      return { stderr: `No manual entry for ${name}` };
    }
    const lines = [`NAME`, `    ${name} — ${cmd.brief}`];
    if (cmd.usage) {
      lines.push('', 'USAGE', `    ${cmd.usage}`);
    }
    return { stdout: lines.join('\n') };
  },
};

// Rich lookup of gag sudo args — ported from the old Easter Egg Bot pane
// (which used to host these as slash commands). Keys are normalised:
// lowercase + single-spaced.
const SUDO_TABLE: Record<string, string[]> = {
  'hire-me': [
    '[sudo] password for thanh.nguyen: ********',
    "Granted. Thanh's calendar is now open — check linkedin.com/in/ngtanthanh",
  ],
  'hire me': [
    '[sudo] password for thanh.nguyen: ********',
    "Granted. Thanh's calendar is now open — check linkedin.com/in/ngtanthanh",
  ],
  'rm -rf bugs': [
    "[sudo] error: 'bugs' is a protected resource (job security).",
  ],
  'rm -rf /': [
    '[sudo] nice try. You are not in the sudoers file. This incident has been reported. And screenshotted. And printed out and framed.',
  ],
  'deploy friday': ["[sudo] DECLINED. Even root can't deploy on Friday."],
  'deploy on friday': ["[sudo] DECLINED. Even root can't deploy on Friday."],
  'skip-tests': [
    '[sudo] permission denied: this incident has been reported to the QA gods.',
  ],
  'skip tests': [
    '[sudo] permission denied: this incident has been reported to the QA gods.',
  ],
  'make me a sandwich': ['🥪'],
  'make sandwich': ['[sudo] OK.', '🥪'],
  'apt install confidence': [
    '[sudo] confidence is not in any configured repository. Try `pip install impostor-syndrome` for a closer match.',
  ],
  'kill -9 monday': ['[sudo] signal sent. Monday ignored it.'],
  'fix flaky test': [
    '[sudo] re-running 3 times to see if it passes...',
    '[sudo] it passed. Closing ticket.',
  ],
};

const sudo: Command = {
  brief: 'gain superpowers (allegedly)',
  usage: 'sudo <command>   (try: hire-me, deploy friday, skip-tests)',
  handler: (args, ctx) => {
    if (args.length === 0) {
      return { stderr: 'usage: sudo <command>' };
    }
    // Normalised lookup first — covers the gag table above.
    const raw = args.join(' ');
    const norm = raw.trim().toLowerCase().replace(/\s+/g, ' ');
    const gag = SUDO_TABLE[norm];
    if (gag) return { stdout: gag.join('\n') };

    // Real-command pass-through — `sudo ls` etc. just re-runs the underlying
    // command with a banner reminder that root is theatre here.
    const [cmdName, ...rest] = args;
    const sub = COMMANDS[cmdName];
    if (sub) {
      const result = sub.handler(rest, ctx);
      const banner =
        "[sudo] just running it as you — you're already root in this terminal.";
      const mergedStdout = result.stdout
        ? `${banner}\n${result.stdout}`
        : banner;
      return { ...result, stdout: mergedStdout };
    }

    // Unknown — generic jokey refusal.
    return {
      stdout: [
        `[sudo] password for thanh.nguyen: ********`,
        `[sudo] '${raw}': command not in the sudoers file for Thanh.`,
        `[sudo] this incident has been logged, ignored, and immediately forgotten.`,
      ].join('\n'),
    };
  },
};

// ---------------------------------------------------------------------------
// Gag commands ported from the retired Easter Egg Bot chat pane.
// ---------------------------------------------------------------------------

const JOKES: string[] = [
  'Why did the QA engineer cross the road? To test it. Then again. Then with edge cases. Then on Internet Explorer. The chicken is now in production.',
  'A QA walks into a bar. Orders 1 beer. Orders 999999 beers. Orders -1 beers. Orders a lizard. Orders NaN beers. Customer #1 walks in and asks where the bathroom is. The bar explodes.',
  "There are 10 types of testers: those who test for edge cases and those who don't realize they ARE an edge case.",
  "I told my manager I was 95% done. They asked about the other 5%. I said it's a known issue with no reproduction steps.",
  'Selenium walks into a bar. The bar element is not interactable at this point. Selenium retries 3 times then gives up.',
  "How many DevOps engineers does it take to change a lightbulb? None — that's an infrastructure-as-code problem. Please open a ticket.",
  "Why did the Python developer get cold? Because they didn't import any sweaters. (The QA filed a bug: 'sweater module exists but is unmaintained since 2017'.)",
  "A networking engineer's wife asks: 'Pick up some bread on the way home, and if they have eggs, get a dozen.' He comes home with 12 loaves. The wife asks why. He says: 'They had eggs.'",
  "Why don't QA engineers play hide and seek? Because good luck hiding when someone is actively looking for bugs.",
  'Production is just a staging environment with feelings.',
  "I don't always test my code. But when I do, I do it in production. — Most Interesting Dev In The World",
  'kubectl get pods returned 47 results. 3 are running. 2 are crashed. 42 are pending. 0 are my problem until standup tomorrow.',
];

const joke: Command = {
  brief: 'random QA / dev joke',
  handler: () => {
    const pick = JOKES[Math.floor(Math.random() * JOKES.length)];
    return { stdout: `[joke] ${pick}` };
  },
};

const QUOTES: string[] = [
  '"Premature optimization is the root of all evil." — Donald Knuth',
  '"Simplicity is prerequisite for reliability." — Edsger Dijkstra',
  '"Testing shows the presence, not the absence of bugs." — Edsger Dijkstra',
  '"The competent programmer is fully aware of the limited size of his own skull." — Edsger Dijkstra',
  '"In theory there is no difference between theory and practice. In practice there is." — Yogi Berra (allegedly)',
  '"Code is poetry. Tests are the rhyme scheme." — Anonymous',
  '"99% of bugs come from the developer. The other 1% are cosmic rays." — every QA ever',
  '"Works on my machine is a valid bug-close reason in exactly zero serious orgs." — internal Slack',
  '"There are two hard problems in computer science: cache invalidation, naming things, and off-by-one errors." — Anonymous',
  '"A flaky test is just a test that hasn\'t admitted what it really is yet." — internal Slack',
];

const quote: Command = {
  brief: 'random dev wisdom (allegedly)',
  handler: () => {
    const pick = QUOTES[Math.floor(Math.random() * QUOTES.length)];
    return { stdout: `[quote] ${pick}` };
  },
};

const pytest: Command = {
  brief: 'run the "thanh coverage" suite (fake)',
  handler: () => ({
    stdout: [
      '============== test session starts ==============',
      'platform darwin -- Python 3.12.0, pytest-8.0.0',
      'collecting tests from thanh.spec ........ 142 items',
      '',
      'test_personality ........................... ok',
      'test_qa_competence ......................... ok',
      'test_python_depth .......................... ok',
      'test_devops_tooling ........................ ok',
      'test_networking_fundamentals ............... ok',
      'test_can_exit_vim .......................... PASSED (rare)',
      'test_replies_to_email_in_under_24h ......... XFAIL (working as intended)',
      'test_deploys_on_friday ..................... SKIPPED (refused)',
      '',
      '============= 141 passed, 1 xfailed, 1 skipped in 0.42s ====',
      "coverage: 97% — missing branches in module 'sleep_schedule'",
    ].join('\n'),
  }),
};

// Forward-declare so help can iterate the registry it lives in.
export const COMMANDS: Record<string, Command> = {
  ls,
  cd,
  cat,
  tree: treeCmd,
  pwd,
  clear,
  help: {
    brief: 'list available commands',
    handler: () => {
      const names = Object.keys(COMMANDS).sort();
      const width = Math.max(...names.map((n) => n.length));
      const lines = names.map(
        (n) => `  ${n.padEnd(width, ' ')}  ${COMMANDS[n].brief}`,
      );
      return {
        stdout: [
          'Available commands:',
          ...lines,
          '',
          'Try: ls, cat README.md, tree, open resume.pdf, exit',
        ].join('\n'),
      };
    },
  },
  history,
  find,
  whoami,
  date: dateCmd,
  open,
  exit,
  vim,
  man,
  sudo,
  joke,
  quote,
  pytest,
  rubik,
  log: rubik,
  timeline: rubik,
};

// ---------------------------------------------------------------------------
// Tab completion
// ---------------------------------------------------------------------------

export interface CompletionResult {
  /** Replacement value for the input box. Same as input if nothing to do. */
  newInput: string;
  /** When multiple matches exist, these are listed to stdout. */
  suggestions?: string[];
}

function commonPrefix(strs: string[]): string {
  if (strs.length === 0) return '';
  let prefix = strs[0];
  for (let i = 1; i < strs.length; i++) {
    while (!strs[i].startsWith(prefix)) {
      prefix = prefix.slice(0, -1);
      if (!prefix) return '';
    }
  }
  return prefix;
}

/**
 * Replace the last whitespace-delimited token in `input` with `replacement`.
 * If `input` ends in a space (i.e. the cursor is sitting in a new empty
 * token), append instead of overwriting the previous token.
 */
function replaceLastToken(input: string, replacement: string): string {
  if (input === '' || /\s$/.test(input)) return input + replacement;
  const m = input.match(/^(.*\s)(\S+)$/);
  if (!m) return replacement;
  return m[1] + replacement;
}

/**
 * Compute tab completion for a shell input line.
 *
 * Position rules:
 *   - input is empty OR has no spaces yet -> command-name completion
 *   - otherwise -> path completion on the last token, relative to cwd
 *
 * When exactly one match exists, the input is rewritten directly. When
 * several match, we extend to the common prefix and also return the list
 * for the caller to render as stdout.
 */
export function complete(input: string, cwd: string): CompletionResult {
  const endsWithSpace = /\s$/.test(input);
  const tokens = tokenize(input);

  // Command-name position: first token, still being typed.
  if (tokens.length <= 1 && !endsWithSpace) {
    const prefix = tokens[0] ?? '';
    const matches = Object.keys(COMMANDS)
      .filter((c) => c.startsWith(prefix))
      .sort();
    if (matches.length === 0) return { newInput: input };
    if (matches.length === 1) return { newInput: matches[0] + ' ' };
    const common = commonPrefix(matches);
    const expanded = common.length > prefix.length ? common : prefix;
    return { newInput: expanded, suggestions: matches };
  }

  // Path-arg position.
  const lastToken = endsWithSpace ? '' : tokens[tokens.length - 1];
  const lastSlash = lastToken.lastIndexOf('/');
  const dirPart = lastSlash >= 0 ? lastToken.slice(0, lastSlash + 1) : '';
  const namePart = lastSlash >= 0 ? lastToken.slice(lastSlash + 1) : lastToken;

  // Resolve dirPart relative to cwd. Special-case "~" alone so we list home.
  const dirAbs = resolvePath(dirPart || '.', cwd);
  if (!dirAbs) return { newInput: input };
  const entries = readDir(dirAbs);
  if (!entries) return { newInput: input };

  const matches = entries.filter((e) => e.name.startsWith(namePart));
  if (matches.length === 0) return { newInput: input };
  if (matches.length === 1) {
    const e = matches[0];
    const repl = dirPart + e.name + (e.type === 'dir' ? '/' : ' ');
    return { newInput: replaceLastToken(input, repl) };
  }
  const common = commonPrefix(matches.map((m) => m.name));
  const expanded =
    common.length > namePart.length
      ? replaceLastToken(input, dirPart + common)
      : input;
  return {
    newInput: expanded,
    suggestions: matches.map((m) => m.name + (m.type === 'dir' ? '/' : '')),
  };
}
